import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { Row, Checkbox, Col, Form, Input, Select, AutoComplete } from 'antd';
import { GlobalIcon, IconImage, GlobalButton as Button } from 'src/modules/global';
import axios from 'axios';
import { useIntl } from 'react-intl';
import querystring from 'query-string';
import { rowDefaultProps } from 'src/utils/grid';
import styles from './GlobalFormContact.module.scss';
import { DEFAULT_INPUT_ORDER, GlobalFormContactProps } from './types';
import { Locale } from 'src/modules/locale';
import countryCodesGerman from 'src/modules/global/components/forms/newsletter/countryCodesGerman';
import countryCodesEnglish from 'src/modules/global/components/forms/newsletter/countryCodesEnglish';

export type GlobalFormContactBodyControls = {
  setCheckPolicy: (val: boolean) => void;
  setSubmitClick: (val: boolean) => void;
};
interface GlobalFormContactBodyProps extends GlobalFormContactProps {
  getControls?: (controls: GlobalFormContactBodyControls) => void;
  onSubmit: (success: boolean) => void;
}

const countriesGerman = _.sortBy(
  _.map(countryCodesGerman, ({ name }) => {
    return { value: name };
  }),
  (item) => item.value
);

const countriesEnglish = _.sortBy(
  _.map(countryCodesEnglish, ({ name }) => {
    return { value: name };
  }),
  (item) => item.value
);

const getCountryCode = (countryName: string, locale: Locale) => {
  let countryCode: { name: string; code: string };

  if (locale === 'de') {
    countryCode = _.find(countryCodesGerman, ({ name }) => name === countryName);
  } else {
    countryCode = _.find(countryCodesEnglish, ({ name }) => name === countryName);
  }

  if (!countryCode?.code) {
    console.error('Cannot determine country code for', countryName, locale);
    return '';
  }

  return countryCode.code;
};

const GlobalFormContactBody: React.FunctionComponent<GlobalFormContactBodyProps> = ({
  data,
  url,
  topJsx = null,
  additionalFormFields = {},
  onlyShowZipcodeForGermany = true,
  customValidator,
  inputsOrder: _inputsOrder = DEFAULT_INPUT_ORDER,
  streetAndNumberRequired = false,
  getControls = _.noop,
  onSubmit,
}: GlobalFormContactBodyProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const router = useRouter();
  const [checkPolicy, setCheckPolicy] = useState(false);
  const [submitClick, setSubmitClick] = useState(false);
  const [showZipCode, setShowZipCode] = useState(!onlyShowZipcodeForGermany);

  useEffect(() => {
    getControls({
      setCheckPolicy,
      setSubmitClick,
    });
  }, [getControls, setCheckPolicy, setSubmitClick]);

  const onValuesChange = (args: any) => {
    if (!onlyShowZipcodeForGermany) return;

    const country = args?.country_code;
    if (!_.isString(country)) return;

    if (country.match(/germany|deutschland/i)) {
      setShowZipCode(true);
    } else {
      setShowZipCode(false);
    }
  };

  // remove zipCode if needed from inputOrder
  const inputsOrder = _.filter(_inputsOrder, (input) => showZipCode || input !== 'zipCode');

  const suffixIcon = (
    <GlobalIcon image={IconImage.DownArrow} size="custom" className={styles.downArrow} />
  );

  const onFinish = async (values: any, locale: Locale) => {
    values.country_code = getCountryCode(values.country_code, locale);

    const customValidatorSuccess = customValidator ? customValidator() : true;
    if (!customValidatorSuccess || !checkPolicy) return;

    try {
      const axiosData = { ...values, ...data, ...additionalFormFields };

      await axios.post(url, querystring.stringify(axiosData), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      onSubmit(true);
    } catch (error) {
      onSubmit(true);
      console.error(error);
    }
  };

  const { Option } = Select;

  const onSubmitClick = () => {
    const customValidatorSuccess = customValidator ? customValidator() : true;
    if (!customValidatorSuccess) return;

    setSubmitClick(true);
  };

  const inputsJsx = {
    salutation: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/salutation' })}
        name="salutation"
        hasFeedback>
        <Select
          suffixIcon={suffixIcon}
          placeholder={intl.formatMessage({ id: 'form/salutation_placeholder' })}>
          <Option value="Mr.">{intl.formatMessage({ id: 'form/salutation/mr' })}</Option>
          <Option value="Ms.">{intl.formatMessage({ id: 'form/salutation/ms' })}</Option>
        </Select>
      </Form.Item>
    ),
    empty: <></>,
    firstName: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/first_name' })}
        name="first_name"
        rules={[{ required: true, whitespace: true }]}>
        <Input type="text" max={40} />
      </Form.Item>
    ),
    lastName: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/last_name' })}
        name="last_name"
        rules={[{ required: true, whitespace: true }]}>
        <Input type="text" max={40} />
      </Form.Item>
    ),
    email: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/email' })}
        name="email"
        rules={[{ required: true, whitespace: true, type: 'email' }]}>
        <Input max={80} type="text" />
      </Form.Item>
    ),
    company: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/company' })}
        name="company"
        rules={[{ whitespace: true }]}>
        <Input type="text" max={40} />
      </Form.Item>
    ),
    country: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/country' })}
        name="country_code"
        hasFeedback
        rules={[{ required: true, whitespace: true, message: 'Please select your country!' }]}>
        {router.locale === 'de' ? (
          <AutoComplete
            options={countriesGerman}
            filterOption={(inputValue, option) =>
              option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
            }
          />
        ) : (
          <AutoComplete
            options={countriesEnglish}
            filterOption={(inputValue, option) =>
              option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
            }
          />
        )}
      </Form.Item>
    ),
    zipCode: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/zipcode' })}
        name="00N3X00000LwRSm"
        rules={[{ required: true, max: 10 }]}
        hasFeedback>
        <Input type="number" />
      </Form.Item>
    ),
    phoneNumber: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/phoneNumber' })}
        name="phone"
        rules={[{ max: 20 }]}
        hasFeedback>
        <Input type="number" />
      </Form.Item>
    ),
    city: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/city' })}
        name="00N3X00000LwRSO"
        rules={[{ required: true, whitespace: true }]}>
        <Input type="text" />
      </Form.Item>
    ),
    streetAndNumber: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/streetAndNumber' })}
        name="00N3X00000LwRSh"
        rules={[{ required: streetAndNumberRequired, whitespace: true }]}>
        <Input type="text" />
      </Form.Item>
    ),
    questions: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/topic' })}
        name="00N3X00000LwRSM"
        hasFeedback
        rules={[{ required: true }]}>
        <Select
          suffixIcon={suffixIcon}
          placeholder={intl.formatMessage({ id: 'form/topic_placeholder' })}>
          <Option value="I have a general question">
            {intl.formatMessage({ id: 'form/topic/general_question' })}
          </Option>
          <Option value="I would like project support">
            {intl.formatMessage({ id: 'form/topic/project_support' })}
          </Option>
          <Option value="I would like planning support">
            {intl.formatMessage({ id: 'form/topic/planning_support' })}
          </Option>
          <Option value="I am searching for CAD-Data files">
            {intl.formatMessage({ id: 'form/topic/cad_search' })}
          </Option>
          <Option value="Where can I find a product?">
            {intl.formatMessage({ id: 'form/topic/where_product' })}
          </Option>
          <Option value="Can you tell me more about a product?">
            {intl.formatMessage({ id: 'form/topic/more_info' })}
          </Option>
        </Select>
      </Form.Item>
    ),
    message: (
      <Form.Item
        label={intl.formatMessage({ id: 'form/message' })}
        name="00N3X00000LwRSZ"
        rules={[{ required: true }]}>
        <Input.TextArea wrap="soft" />
      </Form.Item>
    ),
  };

  const inputsOrderJsx = _.map(inputsOrder, (inputName, i) => {
    if (_.includes(['questions', 'message'], inputName)) {
      return (
        <Col key={i} xs={{ span: 24, offset: 0 }} md={{ span: 12, offset: 6 }}>
          {inputsJsx[inputName]}
        </Col>
      );
    }
    if (i % 2 === 0) {
      return (
        <Col key={i} xs={{ span: 24, offset: 0 }} md={{ span: 6, offset: 6 }}>
          {inputsJsx[inputName]}
        </Col>
      );
    }
    return (
      <Col key={i} xs={24} md={6}>
        {inputsJsx[inputName]}
      </Col>
    );
  });

  return (
    <Form
      layout="vertical"
      size="large"
      form={form}
      onValuesChange={onValuesChange}
      onFinish={(value) => onFinish(value, router.locale as Locale)}>
      {topJsx && (
        <Row {...rowDefaultProps}>
          <Col xs={{ span: 24, offset: 0 }} md={{ span: 12, offset: 6 }}>
            {topJsx}
          </Col>
        </Row>
      )}
      <Row {...rowDefaultProps}>
        {inputsOrderJsx}
        <Col
          className={styles.checkboxCol}
          xs={{ span: 24, offset: 0 }}
          md={{ span: 12, offset: 6 }}>
          <Form.Item rules={[{ required: true }]}>
            <Checkbox
              className={submitClick && !checkPolicy ? styles.errorCheckbox : ''}
              checked={checkPolicy}
              onChange={(e) => setCheckPolicy(e.target.checked)}>
              <div
                className={styles.checkboxMessage}
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({ id: 'form/checkboxPolicy' }),
                }}
              />
            </Checkbox>
          </Form.Item>
        </Col>
        <Col xs={{ span: 24, offset: 0 }} md={{ span: 6, offset: 6 }}>
          <Button type="primary" htmlType="submit" widthType="fullWidth" onClick={onSubmitClick}>
            {intl.formatMessage({ id: 'form/buttonSend' })}
          </Button>
        </Col>
        <Col xs={{ span: 24, offset: 0 }} md={{ span: 12, offset: 6 }}>
          <span className={styles.mandatoryFields}>
            {intl.formatMessage({ id: 'form/mandatoryFields' })}
          </span>
        </Col>
      </Row>
    </Form>
  );
};

export default GlobalFormContactBody;
